import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

interface Parcel {
  id: string;
  destination: string;
  label: string;
  color: string;
}

interface MapPoint {
  id: string;
  label: string;
  top: string;
  left: string;
  color: string;
  index?: number;
}

interface RoutePlanningProps {
  question: string;
  parcels: Parcel[];
  mapPoints: MapPoint[];
  onComplete: (score: number, timeTaken: number) => void;
}

const RoutePlanning: React.FC<RoutePlanningProps> = ({
  question,
  parcels,
  mapPoints,
  onComplete,
}) => {
  const [selectedParcel, setSelectedParcel] = useState<string | null>(null);
  const [isStarted, setIsStarted] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [feedbackType, setFeedbackType] = useState<"success" | "error" | "">(
    ""
  );
  const [randomizedParcels, setRandomizedParcels] = useState<Parcel[]>([]);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [_timeTaken, setTimeTaken] = useState<number>(0);
  const [correctMatches, setCorrectMatches] = useState<
    { parcel: string; destination: string }[]
  >([]);
  const [incorrectDestination, setIncorrectDestination] = useState<
    string | null
  >(null);

  // Helper function to shuffle array
  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // Add index property to map points if not present
  const indexedMapPoints = React.useMemo(() => {
    return mapPoints.map((point, index) => ({
      ...point,
      index,
    }));
  }, [mapPoints]);

  // Separate sets of colors for parcels and circles
  const parcelColors = React.useMemo(
    () => [
      "#FF5733", // Red-orange
      "#33FF57", // Green
      "#3357FF", // Blue
      "#FF33F5", // Pink
      "#F1C40F", // Yellow
      "#E74C3C", // Red
      "#16A085", // Teal
      "#2980B9", // Blue
      "#8E44AD", // Purple
      "#D35400", // Orange
    ],
    []
  );

  const circleColors = React.useMemo(
    () => [
      "#9B59B6", // Purple
      "#27AE60", // Green
      "#3498DB", // Light Blue
      "#F39C12", // Orange
      "#1ABC9C", // Turquoise
      "#C0392B", // Dark Red
      "#2C3E50", // Navy Blue
      "#7D3C98", // Dark Purple
      "#138D75", // Dark Teal
      "#D4AC0D", // Dark Yellow
    ],
    []
  );

  // Assign colors to parcels and map points
  useEffect(() => {
    // Create a copy of parcels with assigned colors
    const parcelsWithColors = parcels.map((parcel, index) => ({
      ...parcel,
      color: parcelColors[index % parcelColors.length],
    }));

    // Randomize and set the parcels
    setRandomizedParcels(shuffleArray(parcelsWithColors));
  }, [parcels, parcelColors]);

  // Reset state when parcels change
  useEffect(() => {
    setSelectedParcel(null);
    setIsComplete(false);
    setScore(0);
    setFeedback("");
    setFeedbackType("");
    setCorrectMatches([]);
    setIncorrectDestination(null);
    setStartTime(null);
    setTimeTaken(0);
  }, [parcels]);

  const startTask = () => {
    setIsStarted(true);
    setStartTime(Date.now());
  };

  const handleParcelClick = (parcelId: string) => {
    // Allow selection if parcel is not in a correct match
    if (correctMatches.some((match) => match.parcel === parcelId)) {
      // Parcel already correctly matched
      return;
    }
    setSelectedParcel(parcelId);
  };

  const handleDestinationClick = (destinationId: string) => {
    if (!selectedParcel) return;

    // Find the parcel object
    const parcel = parcels.find((p) => p.id === selectedParcel);
    if (!parcel) return;

    // Check if the destination is correct
    const isCorrect = parcel.destination === destinationId;

    // For correct matches
    if (isCorrect) {
      // Add to correct matches
      setCorrectMatches((prev) => [
        ...prev,
        { parcel: selectedParcel, destination: destinationId },
      ]);

      // Update score only
      setScore((prev) => prev + 1);
    }
    // For incorrect matches
    else {
      // Temporarily show red background with white text
      setIncorrectDestination(destinationId);

      // Update score only
      setScore((prev) => Math.max(0, prev - 0.5));

      // Revert back to normal after a short delay
      setTimeout(() => {
        setIncorrectDestination(null);
      }, 1000);
    }

    // Clear selection
    setSelectedParcel(null);

    // No feedback to show

    // Check if all parcels are correctly matched
    const allCorrectlyMatched =
      correctMatches.length + (isCorrect ? 1 : 0) === parcels.length;

    if (allCorrectlyMatched) {
      // Complete the task after a short delay
      setTimeout(() => {
        setIsComplete(true);
        const endTime = Date.now();
        const calculatedTimeTaken = startTime
          ? Math.floor((endTime - startTime) / 1000)
          : 0;
        setTimeTaken(calculatedTimeTaken);
        onComplete(score + (isCorrect ? 1 : 0), calculatedTimeTaken);
      }, 1500);
    }
  };

  // No cleanup needed

  return (
    <div className="space-y-6">
      <h2 className="text-2xl mb-6 font-semibold">{question}</h2>

      {!isStarted ? (
        <div className="text-center">
          <button
            onClick={startTask}
            className="bg-[#0F1D21] text-white font-bold py-2 px-6 rounded-lg hover:bg-gray-700 transition-colors duration-300 shadow-md"
          >
            Start Task
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Map with destinations */}
          <div className="relative w-full h-96 bg-gray-100 rounded-lg mb-4">
            {indexedMapPoints.map((point) => (
              <div
                key={point.id}
                className={`absolute w-10 h-10 rounded-full flex items-center justify-center font-bold cursor-pointer`}
                style={{
                  top: point.top,
                  left: point.left,
                  backgroundColor: correctMatches.some(
                    (match) => match.destination === point.id
                  )
                    ? "#808080"
                    : incorrectDestination === point.id
                    ? "#EF4444" // Red background for incorrect
                    : circleColors[point.index % circleColors.length],
                  color:
                    incorrectDestination === point.id
                      ? "white" // White text for incorrect
                      : [
                          "#F39C12",
                          "#F1C40F",
                          "#D4AC0D",
                          "#1ABC9C",
                          "#3498DB",
                        ].includes(
                          circleColors[point.index % circleColors.length]
                        )
                      ? "black"
                      : "white",
                }}
                onClick={() => handleDestinationClick(point.id)}
              >
                {incorrectDestination === point.id ? (
                  <X size={20} />
                ) : (
                  point.label
                )}
              </div>
            ))}
          </div>

          {/* Parcels list */}
          <div className="flex flex-wrap gap-3 justify-center">
            {randomizedParcels.map((parcel) => (
              <div
                key={parcel.id}
                className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold cursor-pointer transition-transform ${
                  correctMatches.some((match) => match.parcel === parcel.id)
                    ? "opacity-50"
                    : "hover:scale-105"
                } ${selectedParcel === parcel.id ? "ring-2 ring-black" : ""}`}
                style={{
                  backgroundColor: correctMatches.some(
                    (match) => match.parcel === parcel.id
                  )
                    ? "#808080"
                    : parcel.color,
                  color: ["#FFBE37", "#FACC15", "#BCDEEF"].includes(
                    parcel.color
                  )
                    ? "black"
                    : "white",
                }}
                onClick={() => handleParcelClick(parcel.id)}
              >
                {parcel.label}
              </div>
            ))}
          </div>

          {/* Feedback */}
          {feedback && (
            <div className="mt-4 text-center">
              <p
                className={`text-lg font-semibold ${
                  feedbackType === "success" ? "text-green-600" : "text-red-600"
                }`}
              >
                {feedback}
              </p>
            </div>
          )}

          {/* Complete state */}
          {isComplete && (
            <div className="mt-6 text-center">
              <p className="text-xl font-bold">Task Complete!</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RoutePlanning;
